import { Auth, Hub } from "aws-amplify";
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { captureMessage, setUser } from "@sentry/react";

import {
  ICognitoUser,
  IPerson,
  RoleUser,
  TCustomOAuthState
} from "../interfaces/interfaces";
import {
  useLocation,
  useNavigate,
  matchPath,
  generatePath
} from "react-router-dom";
import {
  HOST_HOUSE_WIZARD_ROUTE,
  HOST_PROFILE_EDIT_FIRST_ROUTE,
  HOST_PROFILE_VIEW_ROUTE,
  HOST_WIZARD_ROUTE,
  LOGIN_ROUTE,
  STUDENT_PROFILE_EDIT_FIRST_ROUTE,
  STUDENT_ROOMS_ROUTE,
  STUDENT_WIZARD_ROUTE
} from "utils/constants";
import {
  clearLocalStorage,
  createCognitoUserFromAttributes
} from "../utils/utilAmplify";
import { publicRoutes, publicRoutesHospi } from "../routes";
import queryClient from "../stores/queryClient";
import { UserAPI } from "../API/userApi";
import { generateErrorResponse } from "../utils/helpers";
import { HouseAPI } from "../API/houseApi";
import { IWizardHouseStateShort } from "../components/Wizard/interfaces";
import { useNotifier } from "./ContextNotifier";

interface IContextHospi {
  cognitoUser: ICognitoUser | null;
  currentUserId: string | undefined;
  currentUserName: string | undefined;
  currentUserEmail: string | undefined;
  currentUserRole: RoleUser | undefined;
  isLoadingCognitoUser: boolean;
  isLoadingCurrentUser: boolean | "";
  userProfile: IPerson | undefined;
  updateProfile: Dispatch<SetStateAction<IPerson | undefined>>;
  signUp: (
    email: string,
    password: string,
    role: string
  ) => Promise<{ id: string }>;
  signIn: (email?: string, password?: string) => Promise<ICognitoUser>;
  forgotPassword: (email: string) => Promise<string>;
  forgotPasswordSubmit: (
    email: string,
    code: string,
    newPassword: string
  ) => Promise<string>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<string>;
  logOut: (navigateToLogin?: boolean) => void;
  notFinishedRequiredHouseWizard: IWizardHouseStateShort | null;
  cleanNotFinishedRequiredHouseWizard: () => void;
  setNotFinishedRequiredHouseWizard: (wizard: IWizardHouseStateShort) => void;
  refreshSession: () => Promise<ICognitoUser | undefined>;
}

const ContextHospi = createContext<IContextHospi>({
  cognitoUser: null,
  currentUserId: undefined,
  isLoadingCognitoUser: true,
  isLoadingCurrentUser: true,
  currentUserEmail: undefined,
  currentUserRole: undefined,
  currentUserName: "",
  userProfile: undefined,
  updateProfile: () => undefined,
  signUp: () => new Promise((r) => r({ id: "" })),
  signIn: () =>
    new Promise((r) =>
      r({
        id: "",
        email: "",
        email_verified: false,
        role: "host",
        locale: "",
        isSocialAuthUser: false
      })
    ),
  forgotPassword: () => new Promise((r) => r("")),
  forgotPasswordSubmit: () => new Promise((r) => r("")),
  changePassword: () => new Promise((r) => r("")),
  logOut: () => undefined,
  notFinishedRequiredHouseWizard: null,
  cleanNotFinishedRequiredHouseWizard: () => undefined,
  setNotFinishedRequiredHouseWizard: () => undefined,
  refreshSession: () => new Promise((r) => r(undefined))
});

export const useContextHospi = (): IContextHospi => {
  return useContext<IContextHospi>(ContextHospi);
};

const signOut = async () => {
  queryClient.removeQueries();
  clearLocalStorage();
  sessionStorage.removeItem("isFreeStudentInfo");
  await Auth.signOut({ global: true });
};

export const ContextHospiProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cognitoUser, setCognitoUser] = useState<ICognitoUser | null>(null);
  const [profileData, setProfileData] = useState<IPerson | undefined>(
    undefined
  );
  const [isLoadingCognitoUser, setIsLoadingCognitoUser] = useState(true);
  const [isLoadingProfileData, setIsLoadingProfileData] = useState(false);
  const [notFinishedRequiredHouseWizard, setNotFinishedRequiredHouseWizard] =
    useState<IWizardHouseStateShort | null>(null);

  const { t, i18n } = useTranslation();
  const notifier = useNotifier();
  let timer: ReturnType<typeof setTimeout>;

  const updateAndSignInSocialAuthUser = useCallback(
    async (user: any, customState: TCustomOAuthState) => {
      const currentAuthenticatedUser = await Auth.currentAuthenticatedUser();
      if (!currentAuthenticatedUser?.attributes?.profile) {
        const { language } = i18n;
        if (customState === "login") {
          navigate(
            generatePath(`${LOGIN_ROUTE}?unknownProfile=True`, {
              lng: language
            })
          );
          return;
        } else {
          await Auth.updateUserAttributes(user, {
            locale: language,
            profile: customState
          });
        }
      }

      await signIn();
    },
    [i18n.language]
  );

  useEffect(() => {
    let socialAuthUser: any = null;
    const hubListener = Hub.listen(
      "auth",
      async ({ payload: { event, data } }) => {
        switch (event) {
          case "cognitoHostedUI":
            socialAuthUser = data;
            break;
          case "customOAuthState":
            if (socialAuthUser && data)
              await updateAndSignInSocialAuthUser(socialAuthUser, data);
            break;
          default:
            break;
        }
      }
    );

    const load = async () => {
      const publicRoutesPaths = [...publicRoutes, ...publicRoutesHospi].map(
        (r) => r.path
      );
      // load namespaces async without waiting result
      // preloading namespaces fixes problem with blinking screen caused by Suspense
      i18n.loadNamespaces([
        "hostrooms",
        "student",
        "footer",
        "contact",
        "host",
        "hostVerify",
        "matches",
        "chats"
      ]);
      setIsLoadingCognitoUser(true);
      try {
        const { attributes } = await Auth.currentAuthenticatedUser();
        const cognitoUser = createCognitoUserFromAttributes(attributes);

        if (cognitoUser?.role) {
          setCognitoUser(cognitoUser);
          setIsLoadingProfileData(true);

          try {
            const userProfile = await UserAPI.getUser(cognitoUser.id, {
              showNotificationOnError: false
            });
            setProfileData(userProfile);
          } catch (e) {
            if (
              ![
                ...publicRoutesPaths,
                HOST_WIZARD_ROUTE,
                STUDENT_WIZARD_ROUTE
              ].some((path) => matchPath(path, location.pathname))
            ) {
              const response = generateErrorResponse(e);
              if (response?.status === 404) {
                navigate(
                  generatePath(
                    cognitoUser.role === "student"
                      ? STUDENT_WIZARD_ROUTE
                      : HOST_WIZARD_ROUTE,
                    {
                      lng: i18n.language
                    }
                  )
                );
              } else {
                navigate(`/${i18n.language}/`);
              }
            }
          } finally {
            setIsLoadingProfileData(false);
          }

          if (cognitoUser.role === "host") {
            HouseAPI.getWizardsStateShort().then((wizards) => {
              const notFinishedWizard = wizards.find(
                (w) =>
                  !w.metadata.finished &&
                  (w.wizard_type === "publish" ||
                    w.wizard_type === "description")
              );
              if (notFinishedWizard) {
                setNotFinishedRequiredHouseWizard(notFinishedWizard);
              }
            });
          }
        }
      } catch (e) {
        if (
          !publicRoutesPaths.some((path) => matchPath(path, location.pathname))
        ) {
          navigate(`/${i18n.language}/`);
        }
      } finally {
        setIsLoadingCognitoUser(false);
      }
    };
    load();

    return () => {
      hubListener();
      timer && clearTimeout(timer);
    };
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, role: string) => {
      let result = await Auth.signUp({
        username: email,
        password: password,
        attributes: {
          profile: role,
          email: email,
          locale: i18n.language
        }
      });

      return {
        id: result.userSub
      };
    },
    [i18n.language]
  );

  const signIn = useCallback(
    async (email?: string, password?: string) => {
      setCognitoUser(null);
      setProfileData(undefined);

      let cognitoUserWithAttributes;
      if (email && password) {
        cognitoUserWithAttributes = await Auth.signIn({
          username: email,
          password: password
        });
      } else {
        // social auth flow
        cognitoUserWithAttributes = await Auth.currentAuthenticatedUser();
      }

      const cognitoUser = createCognitoUserFromAttributes(
        cognitoUserWithAttributes.attributes
      );
      if (!email && !password) {
        // social auth flow
        window.dataLayer.push({
          event: "login",
          user_id: cognitoUser.id,
          user_type: cognitoUser.role
        });
      }

      try {
        const userProfile = await UserAPI.getUser(cognitoUser.id, {
          showNotificationOnError: false
        });
        setCognitoUser(cognitoUser);
        setProfileData(userProfile);

        if (
          cognitoUser?.role === "student" &&
          cognitoUser?.account_type !== "premium"
        ) {
          try {
            sessionStorage.setItem("isFreeStudentInfo", "true");
          } catch (err: any) {
            // E.g. sessionStorage may throw QuotaExceededError if is full
            captureMessage(`Session storage error after signing-in`, {
              extra: { error: JSON.stringify(err.toJSON()) }
            });
            console.error("sessionStorage error: ", err);
          }
        }

        if (userProfile.is_profile_complete) {
          // string `room-info/${id}` | "checkout" from URL redirect param (e.g. `http://localhost:3000/gb/login?redirect=room-info/${id}`):
          let redirectUrl: string | null = null;
          try {
            if (sessionStorage.getItem("redirectUrl")) {
              redirectUrl = sessionStorage.getItem("redirectUrl");
              sessionStorage.removeItem("redirectUrl");
            }
          } catch (err) {
            // E.g. sessionStorage may throw QuotaExceededError if is full
            console.error("sessionStorage error", err);
          }
          // "checkout" redirectUrl from session storage is used to redirect students of account_type === "free" to payment page:
          if (
            redirectUrl &&
            !(redirectUrl === "checkout" && cognitoUser?.role === "host") &&
            !(
              redirectUrl === "checkout" &&
              cognitoUser?.role === "student" &&
              cognitoUser?.account_type === "premium"
            )
          ) {
            navigate(
              generatePath("/:lng/" + redirectUrl, {
                lng: i18n.language
              })
            );
          } else {
            if (cognitoUser?.role === "student") {
              timer = setTimeout(() => {
                navigate(
                  generatePath(STUDENT_ROOMS_ROUTE, {
                    lng: i18n.language
                  })
                );
              }, 0);
            } else
              navigate(
                generatePath(HOST_PROFILE_VIEW_ROUTE, {
                  lng: i18n.language
                })
              );
          }
        } else {
          navigate(
            generatePath(
              userProfile.user_type === "student"
                ? STUDENT_PROFILE_EDIT_FIRST_ROUTE
                : HOST_PROFILE_EDIT_FIRST_ROUTE,
              {
                lng: i18n.language
              }
            )
          );
        }

        // do async check for host wizards
        // to make login process more fast, in most cases initial wizards will be finished
        // and there will not be redirecting
        if (cognitoUser?.role === "host") {
          HouseAPI.getWizardsStateShort().then((wizards) => {
            const notFinishedWizard = wizards.find(
              (w) =>
                !w.metadata.finished &&
                (w.wizard_type === "publish" || w.wizard_type === "description")
            );
            if (notFinishedWizard) {
              setNotFinishedRequiredHouseWizard(notFinishedWizard);
              navigate(
                generatePath(HOST_HOUSE_WIZARD_ROUTE, {
                  houseId: notFinishedWizard.house_id,
                  wizardType: notFinishedWizard.wizard_type,
                  lng: i18n.language
                })
              );
            }
          });
        }
      } catch (error) {
        setCognitoUser(cognitoUser);
        setProfileData(undefined);

        const response = generateErrorResponse(error);
        if (response?.status === 404) {
          navigate(
            generatePath(
              cognitoUser.role === "student"
                ? STUDENT_WIZARD_ROUTE
                : HOST_WIZARD_ROUTE,
              {
                lng: i18n.language
              }
            )
          );
        } else {
          signOut().then(() => {
            if (location.pathname === LOGIN_ROUTE) {
              navigate(`/${i18n.language}/`);
            }
          });
        }
      }

      return cognitoUser;
    },
    [i18n.language, location.pathname, navigate]
  );

  const forgotPassword = useCallback((email: string) => {
    setCognitoUser(null);
    return Auth.forgotPassword(email);
  }, []);

  const forgotPasswordSubmit = useCallback(
    (email: string, code: string, password: string) => {
      setCognitoUser(null);
      return Auth.forgotPasswordSubmit(email, code, password);
    },
    []
  );

  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string) => {
      let user = await Auth.currentAuthenticatedUser();
      return Auth.changePassword(user, oldPassword, newPassword);
    },
    []
  );

  const logOut = useCallback(
    (navigateToLogin: boolean = true) => {
      signOut().then(() => {
        setCognitoUser(null);
        setProfileData(undefined);
        if (navigateToLogin) {
          navigate(`/${i18n.language}/`);
        }
      });
    },
    [i18n.language, navigate]
  );

  const refreshSession = useCallback(async () => {
    try {
      const refreshToken = (await Auth.currentSession()).getRefreshToken();
      const currentUser = await Auth.currentAuthenticatedUser({
        bypassCache: true
      });
      const currentCognitoUser = createCognitoUserFromAttributes(
        currentUser.attributes
      );
      setCognitoUser(currentCognitoUser);
      currentUser.refreshSession(refreshToken, (err: any) => {
        if (err) {
          notifier.error({ message: t("sessionNotRefreshed") });
          logOut();
        }
      });
      return currentCognitoUser;
    } catch (_) {
      notifier.error({ message: t("userNotUpdated") });
      logOut();
    }
  }, []);

  useEffect(() => {
    if (!cognitoUser) {
      setUser(null);
      return;
    }
    setUser({
      id: cognitoUser.id,
      email: cognitoUser.email
    });
  }, [cognitoUser]);

  return (
    <ContextHospi.Provider
      value={{
        cognitoUser,
        currentUserId: cognitoUser?.id,
        currentUserEmail: cognitoUser?.email,
        currentUserRole: cognitoUser?.role,
        currentUserName: profileData?.first_name || cognitoUser?.email || "",
        userProfile: profileData,
        isLoadingCognitoUser,
        isLoadingCurrentUser: isLoadingProfileData,
        updateProfile: setProfileData,
        signUp,
        signIn,
        forgotPassword,
        forgotPasswordSubmit,
        changePassword,
        logOut,
        notFinishedRequiredHouseWizard: notFinishedRequiredHouseWizard,
        cleanNotFinishedRequiredHouseWizard: useCallback(() => {
          setNotFinishedRequiredHouseWizard(null);
        }, []),
        setNotFinishedRequiredHouseWizard,
        refreshSession
      }}
    >
      {children}
    </ContextHospi.Provider>
  );
};
